import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CartAddedToast } from '../components/CartAddedToast/CartAddedToast';
import {
  CART_STORAGE_KEY,
  DIGIRASA_CART_CLEARED_EVENT,
} from './cartStorage';
import {
  getCartLineTotal,
  mergeAddOnQuantities,
  normalizeCartLineAddOns,
  normalizeCartLineRow,
  resolveCartLineKey,
} from '../pages/OurMenu/cartAddOnUtils';

const CartContext = createContext();

const CART_TOAST_DURATION_MS = 5500;

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(CART_STORAGE_KEY)) || [];
      return Array.isArray(parsed)
        ? parsed.map(normalizeCartLineRow).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  });

  const [addedId, setAddedId] = useState(null);
  const [cartToast, setCartToast] = useState(null);
  const toastTimerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  // Logout clears storage then fires this event so in-memory items cannot be
  // written back by the effect above.
  useEffect(() => {
    const onCartCleared = () => {
      setCartItems((prev) => (prev.length === 0 ? prev : []));
      setAddedId(null);
    };
    window.addEventListener(DIGIRASA_CART_CLEARED_EVENT, onCartCleared);
    return () => window.removeEventListener(DIGIRASA_CART_CLEARED_EVENT, onCartCleared);
  }, []);

  const dismissCartToast = useCallback(() => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setCartToast(null);
  }, []);

  const showCartToast = useCallback(
    ({ name, isRepeat, cartCount }) => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }

      setCartToast({
        name: name || 'Item',
        isRepeat,
        cartCount,
        key: Date.now(),
      });

      toastTimerRef.current = window.setTimeout(() => {
        toastTimerRef.current = null;
        setCartToast(null);
      }, CART_TOAST_DURATION_MS);
    },
    [],
  );

  useEffect(() => () => dismissCartToast(), [dismissCartToast]);

  const flashAdded = (lineKey) => {
    setAddedId(lineKey);
    setTimeout(() => setAddedId(null), 1200);
  };

  const addToCart = (item, options = {}) => {
    const { showToast = true, quantity: qtyOpt } = options;
    const addQty = Math.max(
      1,
      Math.round(Number(qtyOpt != null ? qtyOpt : item.quantity) || 1),
    );
    const lineKey = resolveCartLineKey(item);
    const incomingAddOns = normalizeCartLineAddOns(item.addOns);
    const line = normalizeCartLineRow({
      ...item,
      cartKey: lineKey,
      addOns: incomingAddOns,
      quantity: addQty,
    });
    const existing = cartItems.find((i) => resolveCartLineKey(i) === lineKey);
    const isRepeat = Boolean(existing);
    const nextCount =
      cartItems.reduce((sum, i) => sum + i.quantity, 0) + addQty;
    const now = Date.now();

    setCartItems((prev) => {
      const prevExisting = prev.find((i) => resolveCartLineKey(i) === lineKey);
      if (prevExisting) {
        return prev.map((i) =>
          resolveCartLineKey(i) === lineKey
            ? normalizeCartLineRow({
                ...i,
                quantity: i.quantity + addQty,
                addOns: mergeAddOnQuantities(i.addOns, incomingAddOns),
                addedAt: now,
              })
            : i,
        );
      }
      return [...prev, { ...line, quantity: addQty, addedAt: now }];
    });

    flashAdded(lineKey);

    const onCartPage =
      typeof window !== 'undefined' && window.location.pathname === '/cart';
    if (showToast && !onCartPage) {
      const addonHint =
        incomingAddOns.length > 0
          ? ` (+ ${incomingAddOns.map((a) => a.name).join(', ')})`
          : '';
      const qtyHint = addQty > 1 ? `${addQty}× ` : '';
      showCartToast({
        name: item.variantLabel
          ? `${qtyHint}${item.name || 'Item'} · ${item.variantLabel}${addonHint}`
          : `${qtyHint}${item.name || 'Item'}${addonHint}`,
        isRepeat,
        cartCount: nextCount,
      });
    }
  };

  const removeFromCart = (cartKey) => {
    const key = String(cartKey);
    setCartItems((prev) => prev.filter((i) => resolveCartLineKey(i) !== key));
  };

  /** Host plate qty only — add-on quantities stay unchanged (mobile “add another plate”). */
  const updateQty = (cartKey, qty) => {
    const key = String(cartKey);
    if (qty < 1) {
      removeFromCart(key);
      return;
    }
    setCartItems((prev) =>
      prev.map((i) =>
        resolveCartLineKey(i) === key
          ? { ...i, quantity: qty, addedAt: Date.now() }
          : i,
      ),
    );
  };

  /**
   * Replace a cart line's configuration (option / add-ons).
   * Pass `quantity` to set plate count from the customise modal; otherwise keeps old qty.
   * If the new cartKey already exists, host + add-on quantities are merged.
   */
  const replaceCartLine = (oldCartKey, nextItem, options = {}) => {
    const oldKey = String(oldCartKey);
    const newKey = resolveCartLineKey(nextItem);
    const now = Date.now();
    const qtyOverride =
      options.quantity != null
        ? Math.max(1, Math.round(Number(options.quantity) || 1))
        : null;
    const nextAddOns = normalizeCartLineAddOns(nextItem.addOns);

    setCartItems((prev) => {
      const oldLine = prev.find((i) => resolveCartLineKey(i) === oldKey);
      if (!oldLine) return prev;

      const quantity =
        qtyOverride != null
          ? qtyOverride
          : Math.max(1, Math.round(Number(oldLine.quantity) || 1));
      const withoutOld = prev.filter((i) => resolveCartLineKey(i) !== oldKey);
      const existingNew = withoutOld.find((i) => resolveCartLineKey(i) === newKey);
      const mergedQty = existingNew
        ? Math.max(1, Number(existingNew.quantity) || 0) + quantity
        : quantity;
      const mergedAddOns = existingNew
        ? mergeAddOnQuantities(existingNew.addOns, nextAddOns)
        : nextAddOns;
      const line = normalizeCartLineRow({
        ...nextItem,
        cartKey: newKey,
        addOns: mergedAddOns,
        quantity: mergedQty,
        addedAt: now,
      });

      if (existingNew) {
        return withoutOld.map((i) =>
          resolveCartLineKey(i) === newKey ? line : i,
        );
      }
      return [...withoutOld, line];
    });

    flashAdded(newKey);
  };

  const clearCart = useCallback(() => {
    setCartItems((prev) => (prev.length === 0 ? prev : []));
  }, []);

  /** Replace entire cart (used for reorder). Each item must include `quantity`. */
  const replaceCart = (items) => {
    const next = (Array.isArray(items) ? items : [])
      .map((item) =>
        normalizeCartLineRow({
          ...item,
          addedAt: Number(item.addedAt) || Date.now(),
        }),
      )
      .filter(Boolean);
    setCartItems(next);
    setAddedId(null);
  };

  const cartCount = cartItems.reduce((sum, i) => sum + i.quantity, 0);
  const cartTotal = cartItems.reduce((sum, i) => sum + getCartLineTotal(i), 0);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQty,
        replaceCartLine,
        clearCart,
        replaceCart,
        cartCount,
        cartTotal,
        addedId,
      }}
    >
      {children}
      <CartAddedToast toast={cartToast} onDismiss={dismissCartToast} />
    </CartContext.Provider>
  );
};
